"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import type { Locale } from "@/translations";
import { t as translate } from "@/translations";
import { LOCALE_COOKIE_NAME } from "@/lib/localeCookie";
import {
  pathnameToCanonical,
  withLocalePrefix,
} from "@/lib/i18nRouting";

const STORAGE_KEY = "petromin-locale";

function persistLocaleCookie(locale: Locale) {
  if (typeof document === "undefined") return;
  try {
    const maxAge = 60 * 60 * 24 * 365;
    document.cookie = `${LOCALE_COOKIE_NAME}=${locale};path=/;max-age=${maxAge};SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

function getStoredLocale(): Locale {
  if (typeof window === "undefined") return "ar";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "ar" || stored === "en") return stored;
  } catch {
    // ignore
  }
  return "ar";
}

type LanguageContextType = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (path: string) => string;
  isRtl: boolean;
};

const LanguageContext = createContext<LanguageContextType | null>(null);

type LanguageProviderProps = {
  children: ReactNode;
  /** From server (cookie) so first paint matches SSR HTML and avoids hydration flashes. */
  initialLocale?: Locale;
};

export function LanguageProvider({ children, initialLocale }: LanguageProviderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? "ar");
  const [mounted, setMounted] = useState(false);

  /** Keep client locale in sync when the root layout re-renders after cookie-based refresh. */
  useEffect(() => {
    setLocaleState(initialLocale ?? "ar");
  }, [initialLocale]);

  useEffect(() => {
    if (initialLocale) {
      setMounted(true);
      return;
    }
    setLocaleState(getStoredLocale());
    setMounted(true);
  }, [initialLocale]);

  useEffect(() => {
    if (!mounted) return;
    document.documentElement.lang = locale === "ar" ? "ar" : "en";
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
    document.body.style.direction = locale === "ar" ? "rtl" : "ltr";
  }, [locale, mounted]);

  const setLocale = useCallback(
    (newLocale: Locale) => {
      setLocaleState(newLocale);
      try {
        localStorage.setItem(STORAGE_KEY, newLocale);
      } catch {
        // ignore
      }
      persistLocaleCookie(newLocale);
      const canonical = pathnameToCanonical(pathname);
      const nextPath = withLocalePrefix(newLocale, canonical);
      if (nextPath !== pathname) {
        router.push(nextPath);
      }
      router.refresh();
    },
    [pathname, router]
  );

  const t = useCallback(
    (path: string) => translate(locale, path),
    [locale]
  );

  return (
    <LanguageContext.Provider
      value={{
        locale,
        setLocale,
        t,
        isRtl: locale === "ar",
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx)
    throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
